/**
 * AssetDetailModal — full-image asset viewer with three tabs:
 *   - Metadata: CES score, persona, scenario, dimensions, model
 * Phase 4: surfaces exemplar template badges + contract adherence scores.
 *   - Pipeline: parent assets that composed this ad (via lineage API)
 *   - Prompt:   copyable generation prompt to recreate in an image model
 *
 * Used from both AssetsLens (quick thumbnail click) and PackView (gallery click).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ArrowUpRight,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  Image as ImageIcon,
  Layers,
  Loader2,
  Palette,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import { getApiBase } from '@/services/api';

// ─── Types (mirrors canvas-primitives wire types) ───────────────────────────

export interface ModalAsset {
  asset_id: string;
  asset_pack_id: string;
  track: string;
  media_type: string;
  media_url: string;
  thumbnail_url: string | null;
  dimensions: { width: number; height: number; aspect_ratio: string } | null;
  grounding: {
    brief_id: string;
    persona_id: string;
    scenario_id: string | null;
    creative_concepts: { id: string; label: string; strength: number }[];
  };
  generation_params: {
    model: string | null;
    prompt_hash: string | null;
    seed: number | null;
    stage: string | null;
    extras: Record<string, unknown>;
  };
  scores: Record<string, number>;
  role_tags: string[];
  created_at: string;
}

interface LineageAsset {
  asset_id: string;
  track: string;
  media_url: string;
  thumbnail_url: string | null;
  generation_params: {
    stage: string | null;
    extras: Record<string, unknown>;
    model: string | null;
    prompt_hash: string | null;
    seed: number | null;
  };
}

const TRACK_LABELS: Record<string, string> = {
  scenario_frame: 'Scene Frame',
  scene_element: 'Scene Element',
  environment_plate: 'Environment',
  product_study: 'Product Study',
  mood_palette: 'Mood Palette',
  composition_frame: 'Composition',
  typography: 'Typography',
  final_ad: 'Final Ad',
};

const GROUPED_TRACKS = [
  'product_study',
  'scenario_frame',
  'environment_plate',
  'composition_frame',
  'scene_element',
] as const;

const AGENT_BASE_URL = getApiBase();

function zipDownloadUrl(packId: string) {
  return `${AGENT_BASE_URL}/api/v1/creative-canvas/asset_pack/${encodeURIComponent(packId)}/export?format=zip`;
}

async function fetchLineage(packId: string, assetId: string) {
  const r = await fetch(
    `${AGENT_BASE_URL}/api/v1/creative-canvas/asset_pack/${encodeURIComponent(packId)}/asset/${encodeURIComponent(assetId)}/lineage`,
  );
  if (!r.ok) return null;
  return r.json() as Promise<{ parents: LineageAsset[] }>;
}

interface FormatTemplateDetail {
  template_id: string;
  template_name: string;
  description: string;
  category_scope: string;
  quality_tier: string;
  funnel_stage: string;
  zone_count: number;
  zone_names: string[];
  recommended_for_channels: string[];
  best_for_scenarios: string[];
  instance_count: number;
  hero_ad_image_url: string | null;
}

async function fetchFormatTemplate(id: string): Promise<FormatTemplateDetail | null> {
  try {
    const r = await fetch(
      `${AGENT_BASE_URL}/api/v1/ad-templates/format-templates/${encodeURIComponent(id)}`,
    );
    if (!r.ok) return null;
    return r.json() as Promise<FormatTemplateDetail>;
  } catch {
    return null;
  }
}

// ─── Prompt recipe helpers ──────────────────────────────────────────────────

interface PromptRecipe {
  raw: string;
  sections: { title: string; content: string }[];
  referenceInputs: {
    productImageUsed: boolean;
    seededSceneUsed: boolean;
    seededSceneProductId: string | null;
    styleReferenceUsed: boolean;
    exemplarCount: number;
    exemplarFraming: string;
  } | null;
  negativePrompt: string | null;
  model: string | null;
}

function parsePromptIntoSections(raw: string): { title: string; content: string }[] {
  const lines = raw.split('\n');
  const sections: { title: string; content: string }[] = [];
  let current: { title: string; lines: string[] } = { title: 'General', lines: [] };
  for (const line of lines) {
    if (line.startsWith('## ')) {
      if (current.lines.length > 0) {
        sections.push({
          title: current.title,
          content: current.lines.join('\n').trim(),
        });
      }
      current = { title: line.slice(3).trim(), lines: [] };
    } else {
      current.lines.push(line);
    }
  }
  if (current.lines.length > 0) {
    sections.push({
      title: current.title,
      content: current.lines.join('\n').trim(),
    });
  }
  return sections.filter((s) => s.content.length > 0);
}

function buildPromptRecipe(asset: ModalAsset): PromptRecipe | null {
  const extras = asset.generation_params?.extras ?? {};
  const candidates = [
    extras['final_prompt'],
    extras['prompt'],
    extras['image_prompt'],
  ];
  const found = candidates.find(
    (c) => typeof c === 'string' && (c as string).length > 0,
  );
  if (!found) return null;
  const raw = found as string;
  // Treat the legacy fallback string as no-prompt-recorded
  if (raw.startsWith('Static ad for')) return null;

  const refRaw = extras['reference_inputs'];
  const refInputs =
    refRaw && typeof refRaw === 'object' && !Array.isArray(refRaw)
      ? (refRaw as Record<string, unknown>)
      : null;

  const negative = extras['negative_prompt'];

  return {
    raw,
    sections: parsePromptIntoSections(raw),
    referenceInputs: refInputs
      ? {
          productImageUsed: Boolean(refInputs['product_image_used']),
          seededSceneUsed: Boolean(refInputs['seeded_scene_used']),
          seededSceneProductId:
            (refInputs['seeded_scene_product_id'] as string | null) ?? null,
          styleReferenceUsed: Boolean(refInputs['style_reference_used']),
          exemplarCount: Number(refInputs['exemplar_count']) || 0,
          exemplarFraming: (refInputs['exemplar_framing'] as string) || '',
        }
      : null,
    negativePrompt: typeof negative === 'string' && negative.length > 0 ? negative : null,
    model: asset.generation_params?.model || null,
  };
}

function ReferenceInputsStrip({
  inputs,
}: {
  inputs: NonNullable<PromptRecipe['referenceInputs']>;
}) {
  const refs: { label: string; icon: ReactNode; present: boolean }[] = [
    { label: 'Product Photo', icon: <Camera size={11} />, present: inputs.productImageUsed },
    { label: 'Scene Context', icon: <ImageIcon size={11} />, present: inputs.seededSceneUsed },
    { label: 'Style Ref', icon: <Palette size={11} />, present: inputs.styleReferenceUsed },
    ...Array.from({ length: inputs.exemplarCount }, (_, i) => ({
      label: `Template Ref ${i + 1}`,
      icon: <Layers size={11} />,
      present: true,
    })),
  ].filter((r) => r.present);

  if (refs.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
        Reference Inputs
      </p>
      <div className="flex flex-wrap gap-1.5">
        {refs.map((r, i) => (
          <div
            key={`${r.label}-${i}`}
            className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-slate-200 bg-slate-50 text-[10px] text-slate-600"
          >
            {r.icon}
            <span>{r.label}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-400 leading-relaxed">
        Attach these images in the same order when running this prompt in another image
        model. Use the asset pack ZIP to retrieve the source files.
      </p>
    </div>
  );
}

function PromptSection({
  title,
  content,
  defaultExpanded,
}: {
  title: string;
  content: string;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      void navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    },
    [content],
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50 transition"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {expanded ? (
            <ChevronDown size={11} className="text-slate-400 shrink-0" />
          ) : (
            <ChevronRight size={11} className="text-slate-400 shrink-0" />
          )}
          <span className="text-[11px] font-semibold text-slate-700 truncate">
            {title}
          </span>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className={clsx(
            'shrink-0 flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-md border transition',
            copied
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:text-slate-700',
          )}
        >
          {copied ? <Check size={10} /> : <Copy size={10} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1">
          <p className="text-[11px] text-slate-700 font-mono whitespace-pre-wrap leading-relaxed select-all">
            {content}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

type Tab = 'metadata' | 'pipeline' | 'prompt';

interface Props {
  asset: ModalAsset;
  packId: string;
  onClose: () => void;
  onNavigateToTemplate?: (templateId: string) => void;
}

export function AssetDetailModal({ asset, packId, onClose, onNavigateToTemplate }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('metadata');
  const [parents, setParents] = useState<LineageAsset[]>([]);
  const [lineageLoading, setLineageLoading] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);
  const [templateDetails, setTemplateDetails] = useState<Map<string, FormatTemplateDetail | null>>(new Map());
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Fetch lineage whenever this tab is opened
  useEffect(() => {
    if (activeTab !== 'pipeline') return;
    let cancelled = false;
    setLineageLoading(true);
    fetchLineage(packId, asset.asset_id)
      .then((data) => {
        if (!cancelled) setParents(data?.parents ?? []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLineageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, packId, asset.asset_id]);

  // Fetch format template details on mount for any template IDs present
  const exemplarTemplateIdsForFetch = useMemo(() => {
    const v = asset.generation_params?.extras?.['exemplar_template_ids'];
    return Array.isArray(v) ? (v.filter((x) => typeof x === 'string') as string[]) : [];
  }, [asset.generation_params]);

  useEffect(() => {
    if (exemplarTemplateIdsForFetch.length === 0) return;
    let cancelled = false;
    setTemplatesLoading(true);
    Promise.all(exemplarTemplateIdsForFetch.map((id) => fetchFormatTemplate(id).then((data) => [id, data] as [string, FormatTemplateDetail | null])))
      .then((results) => {
        if (!cancelled) {
          setTemplateDetails(new Map(results));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => { cancelled = true; };
  }, [exemplarTemplateIdsForFetch]);

  const url = asset.media_url || asset.thumbnail_url;
  const ces = asset.scores?.['ces'];
  const scenario = asset.grounding.scenario_id?.replace(/_/g, ' ') ?? null;
  const persona = asset.grounding.persona_id?.replace(/_/g, ' ') ?? null;

  // Phase 4: Contract adherence (per-slot presence) read from asset extras.
  const contractAdherence = useMemo(() => {
    const extras = asset.generation_params?.extras ?? {};
    const v = extras['contract_adherence'];
    if (!v || typeof v !== 'object') return null;
    return v as {
      template_name?: string;
      overall_score?: number;
      slot_presence_score?: number;
      slots_found?: number;
      slots_required?: number;
      contract_satisfied?: boolean;
      slot_checks?: Array<{
        slot_id: string;
        found: boolean;
        confidence: number;
        rationale: string;
      }>;
      base_adherence?: Record<string, unknown>;
      rationale?: string;
    };
  }, [asset.generation_params]);

  // Per-ad template attribution from the TemplateExemplarResolver — pipeline
  // mirrors these onto generation_params.extras via DigitalAsset.metadata.extras.
  const exemplarTemplateIds = useMemo(() => {
    const v = asset.generation_params?.extras?.['exemplar_template_ids'];
    return Array.isArray(v) ? (v.filter((x) => typeof x === 'string') as string[]) : [];
  }, [asset.generation_params]);
  const exemplarStyleTokenIds = useMemo(() => {
    const v = asset.generation_params?.extras?.['exemplar_visual_style_token_ids'];
    return Array.isArray(v) ? (v.filter((x) => typeof x === 'string') as string[]) : [];
  }, [asset.generation_params]);

  // Extract best available prompt text from generation_params.extras
  const promptText = useMemo(() => {
    const extras = asset.generation_params?.extras ?? {};
    const candidates = [
      extras['prompt'],
      extras['final_prompt'],
      extras['image_prompt'],
      extras['system_prompt'],
      extras['generation_prompt'],
    ];
    const found = candidates.find((c) => typeof c === 'string' && (c as string).length > 0);
    if (found) return found as string;
    // Fall back to all string/number keys as a structured block
    const lines = Object.entries(extras)
      .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
      .map(([k, v]) => `${k}: ${String(v)}`);
    return lines.length > 0 ? lines.join('\n') : null;
  }, [asset.generation_params]);

  const handleCopyPrompt = useCallback(() => {
    if (!promptText) return;
    void navigator.clipboard.writeText(promptText);
    setPromptCopied(true);
    setTimeout(() => setPromptCopied(false), 2200);
  }, [promptText]);

  // Phase 8: structured prompt recipe (sections + reference inputs + settings).
  const recipe = useMemo(() => buildPromptRecipe(asset), [asset]);
  const [recipeAllCopied, setRecipeAllCopied] = useState(false);
  const [recipeNegCopied, setRecipeNegCopied] = useState(false);
  const handleCopyRecipeAll = useCallback(() => {
    if (!recipe) return;
    void navigator.clipboard.writeText(recipe.raw);
    setRecipeAllCopied(true);
    setTimeout(() => setRecipeAllCopied(false), 1800);
  }, [recipe]);
  const handleCopyRecipeNegative = useCallback(() => {
    if (!recipe?.negativePrompt) return;
    void navigator.clipboard.writeText(recipe.negativePrompt);
    setRecipeNegCopied(true);
    setTimeout(() => setRecipeNegCopied(false), 1800);
  }, [recipe]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex"
        style={{ maxWidth: 900, maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Left: Full image ── */}
        <div className="w-[42%] shrink-0 bg-slate-950 flex items-center justify-center overflow-hidden">
          {url ? (
            <img
              src={url}
              alt="Ad asset"
              className="w-full h-full object-contain"
              style={{ maxHeight: '90vh' }}
            />
          ) : (
            <div className="flex items-center justify-center h-64 w-full">
              <ImageIcon size={40} className="text-slate-600" />
            </div>
          )}
        </div>

        {/* ── Right: Detail panel ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-5 pt-5 pb-3 border-b border-slate-100 shrink-0">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="min-w-0">
                {scenario && (
                  <p className="text-[10px] font-bold uppercase tracking-wider text-teal-600 mb-1">
                    {scenario}
                  </p>
                )}
                {ces != null && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                    CES {Math.round(ces * 100)}
                  </span>
                )}
                {contractAdherence?.overall_score != null && (
                  <span
                    className={clsx(
                      'inline-flex items-center gap-1 text-xs font-bold ml-1.5 border px-2 py-0.5 rounded-full',
                      contractAdherence.overall_score >= 0.8
                        ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                        : contractAdherence.overall_score >= 0.65
                          ? 'text-amber-700 bg-amber-50 border-amber-200'
                          : 'text-red-700 bg-red-50 border-red-200',
                    )}
                    title={contractAdherence.rationale || 'Contract adherence score'}
                  >
                    Contract {Math.round((contractAdherence.overall_score ?? 0) * 100)}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition shrink-0"
              >
                <X size={13} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1">
              {(['metadata', 'pipeline', 'prompt'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={clsx(
                    'px-3 py-1.5 text-[11px] font-semibold rounded-lg transition',
                    activeTab === tab
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100',
                  )}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Tab body */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {/* ── Metadata tab ── */}
            {activeTab === 'metadata' && (
              <div className="space-y-3">
                {persona && <MetaRow label="Persona" value={persona} />}
                {scenario && <MetaRow label="Scenario" value={scenario} />}
                <MetaRow label="Media Type" value={asset.media_type} />
                {asset.dimensions && (
                  <MetaRow
                    label="Dimensions"
                    value={`${asset.dimensions.width} × ${asset.dimensions.height} (${asset.dimensions.aspect_ratio})`}
                  />
                )}
                {asset.generation_params?.model && (
                  <MetaRow label="Model" value={asset.generation_params.model} />
                )}
                {asset.generation_params?.stage && (
                  <MetaRow label="Stage" value={asset.generation_params.stage} />
                )}
                {asset.role_tags?.length > 0 && (
                  <MetaRow label="Role Tags" value={asset.role_tags.join(', ')} />
                )}
                <MetaRow label="Asset ID" value={asset.asset_id} mono />
                {asset.grounding.creative_concepts?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-1.5">
                      Creative Concepts
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {asset.grounding.creative_concepts.map((c) => (
                        <span
                          key={c.id}
                          className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full"
                        >
                          {c.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {contractAdherence && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-1.5">
                      Contract Score{contractAdherence.template_name ? ` — ${contractAdherence.template_name}` : ''}
                    </p>
                    <div className="text-[11px] text-slate-700 mb-2">
                      Slots:{' '}
                      <span className="font-semibold">
                        {contractAdherence.slots_found ?? 0}/{contractAdherence.slots_required ?? 0}
                      </span>{' '}
                      satisfied
                      {contractAdherence.slot_presence_score != null && (
                        <span className="text-slate-500">
                          {' '}
                          ({Math.round(contractAdherence.slot_presence_score * 100)}%)
                        </span>
                      )}
                    </div>
                    {contractAdherence.slot_checks && contractAdherence.slot_checks.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {contractAdherence.slot_checks.map((sc) => (
                          <span
                            key={sc.slot_id}
                            title={sc.rationale}
                            className={clsx(
                              'text-[10px] border px-2 py-0.5 rounded-full',
                              sc.found
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-red-50 text-red-700 border-red-200',
                            )}
                          >
                            {sc.slot_id} {sc.found ? '✓' : '✗'}
                          </span>
                        ))}
                      </div>
                    )}
                    {contractAdherence.rationale && (
                      <p className="text-[10px] text-slate-500 italic mt-2">
                        {contractAdherence.rationale}
                      </p>
                    )}
                  </div>
                )}
                {(exemplarTemplateIds.length > 0 ||
                  exemplarStyleTokenIds.length > 0) && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">
                      Templates Applied
                    </p>
                    <div className="space-y-2">
                      {exemplarTemplateIds.map((id) => {
                        const detail = templateDetails.get(id);
                        return (
                          <TemplateCard
                            key={`tpl-${id}`}
                            id={id}
                            detail={detail ?? null}
                            loading={templatesLoading && !templateDetails.has(id)}
                            onNavigate={onNavigateToTemplate}
                          />
                        );
                      })}
                      {exemplarStyleTokenIds.map((id) => (
                        <span
                          key={`sty-${id}`}
                          title={`Visual Style Token — ${id}`}
                          className="inline-block font-mono text-[10px] bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 px-2 py-0.5 rounded-full"
                        >
                          {id}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Pipeline tab ── */}
            {activeTab === 'pipeline' && (
              <div className="space-y-3">
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  The upstream assets that composed this final ad. Each stage
                  contributes reference inputs the generation model combines into
                  the finished creative.
                </p>
                {lineageLoading && (
                  <div className="flex items-center gap-2 text-[11px] text-slate-400 py-6">
                    <Loader2 size={13} className="animate-spin" />
                    Loading pipeline components…
                  </div>
                )}
                {!lineageLoading && parents.length === 0 && (
                  <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-5 text-center space-y-1.5">
                    <p className="text-[11px] text-slate-400 font-medium">
                      No pipeline components recorded
                    </p>
                    <p className="text-[10px] text-slate-300 leading-relaxed">
                      Lineage is captured for packs generated after the pipeline
                      upgrade. Older packs can be backfilled via the API.
                    </p>
                  </div>
                )}
                {!lineageLoading && parents.length > 0 && (
                  <div className="space-y-3">
                    {GROUPED_TRACKS.map((track) => {
                      const trackAssets = parents.filter((p) => p.track === track);
                      if (trackAssets.length === 0) return null;
                      return (
                        <div key={track}>
                          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                            {TRACK_LABELS[track] ?? track.replace(/_/g, ' ')}
                          </p>
                          <div className="space-y-1.5">
                            {trackAssets.map((p) => (
                              <PipelineComponent key={p.asset_id} asset={p} />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {parents
                      .filter(
                        (p) => !(GROUPED_TRACKS as readonly string[]).includes(p.track),
                      )
                      .map((p) => (
                        <PipelineComponent key={p.asset_id} asset={p} />
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Prompt tab ── */}
            {activeTab === 'prompt' && (
              <div className="space-y-3">
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Use this prompt with your image model to recreate or iterate on the
                  concept. For best results, also download the asset pack ZIP and attach the
                  source component images (scene frames, product studies) as reference inputs.
                </p>

                {/* ZIP download */}
                <a
                  href={zipDownloadUrl(packId)}
                  download
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-violet-600 hover:text-violet-700 transition"
                >
                  <Download size={12} /> Download asset pack ZIP
                </a>

                {recipe ? (
                  <div className="space-y-3">
                    {/* Reference inputs strip */}
                    {recipe.referenceInputs && (
                      <ReferenceInputsStrip inputs={recipe.referenceInputs} />
                    )}

                    {/* Sectioned prompt */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Generation Prompt
                        </p>
                        <button
                          type="button"
                          onClick={handleCopyRecipeAll}
                          className={clsx(
                            'flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-md border transition',
                            recipeAllCopied
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:text-slate-700',
                          )}
                        >
                          {recipeAllCopied ? <Check size={10} /> : <Copy size={10} />}
                          {recipeAllCopied ? 'Copied' : 'Copy full prompt'}
                        </button>
                      </div>
                      <div className="space-y-1.5">
                        {recipe.sections.length > 0 ? (
                          recipe.sections.map((s, i) => (
                            <PromptSection
                              key={`${s.title}-${i}`}
                              title={s.title}
                              content={s.content}
                              defaultExpanded={i < 2}
                            />
                          ))
                        ) : (
                          <PromptSection
                            title="Generation Prompt"
                            content={recipe.raw}
                            defaultExpanded
                          />
                        )}
                      </div>
                    </div>

                    {/* Settings */}
                    {(recipe.model || recipe.negativePrompt || recipe.referenceInputs?.exemplarFraming) && (
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Settings
                        </p>
                        <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
                          {recipe.model && (
                            <div className="flex items-start gap-3">
                              <span className="text-[10px] font-bold uppercase text-slate-400 w-24 shrink-0 pt-0.5">
                                Model
                              </span>
                              <span className="text-[11px] font-mono text-slate-700">
                                {recipe.model}
                              </span>
                            </div>
                          )}
                          {recipe.referenceInputs?.exemplarFraming && (
                            <div className="flex items-start gap-3">
                              <span className="text-[10px] font-bold uppercase text-slate-400 w-24 shrink-0 pt-0.5">
                                Exemplar framing
                              </span>
                              <span className="text-[11px] text-slate-700">
                                {recipe.referenceInputs.exemplarFraming.replace(/_/g, ' ')}
                              </span>
                            </div>
                          )}
                          {recipe.negativePrompt && (
                            <div className="flex items-start gap-3">
                              <span className="text-[10px] font-bold uppercase text-slate-400 w-24 shrink-0 pt-0.5">
                                Negative
                              </span>
                              <div className="flex-1 min-w-0 space-y-1">
                                <p className="text-[11px] text-slate-700 font-mono whitespace-pre-wrap leading-relaxed select-all">
                                  {recipe.negativePrompt}
                                </p>
                                <button
                                  type="button"
                                  onClick={handleCopyRecipeNegative}
                                  className={clsx(
                                    'flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-md border transition',
                                    recipeNegCopied
                                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                      : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:text-slate-700',
                                  )}
                                >
                                  {recipeNegCopied ? <Check size={10} /> : <Copy size={10} />}
                                  {recipeNegCopied ? 'Copied' : 'Copy'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : promptText ? (
                  <div className="space-y-2">
                    {/* Legacy flat-prompt fallback for packs that predate the recipe schema */}
                    <div className="relative rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
                      <div className="max-h-56 overflow-y-auto px-4 pt-4 pb-12">
                        <p className="text-[11px] text-slate-800 font-mono whitespace-pre-wrap leading-relaxed select-all">
                          {promptText}
                        </p>
                      </div>
                      <div className="absolute bottom-0 inset-x-0 px-3 py-2 bg-gradient-to-t from-slate-50 via-slate-50/90 to-transparent flex justify-end">
                        <button
                          onClick={handleCopyPrompt}
                          className={clsx(
                            'flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg border transition',
                            promptCopied
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-800 shadow-sm',
                          )}
                        >
                          {promptCopied ? <Check size={11} /> : <Copy size={11} />}
                          {promptCopied ? 'Copied to clipboard!' : 'Copy to clipboard'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-5 text-center space-y-2">
                    <p className="text-[11px] text-slate-500 font-medium">
                      No prompt stored for this asset
                    </p>
                    <p className="text-[10px] text-slate-400 leading-relaxed">
                      This pack was generated before prompt tracing was enabled. The ZIP
                      download above contains the source component images — drop them into
                      your image model with a brief description to recreate the concept.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Pipeline component card ─────────────────────────────────────────────────

function PipelineComponent({ asset }: { asset: LineageAsset }) {
  const url = asset.thumbnail_url || asset.media_url;
  const label = TRACK_LABELS[asset.track] ?? asset.track.replace(/_/g, ' ');

  return (
    <div className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
      <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-slate-200">
        {url ? (
          <img src={url} alt={label} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon size={14} className="text-slate-300" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-violet-600 mb-0.5">
          {label}
        </p>
        <p className="font-mono text-[10px] text-slate-400 truncate">
          {asset.asset_id.slice(0, 24)}…
        </p>
        {asset.generation_params?.stage && (
          <p className="text-[10px] text-slate-500 mt-1">
            Stage: {asset.generation_params.stage}
          </p>
        )}
        {asset.generation_params?.model && (
          <p className="text-[10px] text-slate-400 mt-0.5">
            Model: {asset.generation_params.model}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── TemplateCard ────────────────────────────────────────────────────────────

function TemplateCard({
  id,
  detail,
  loading,
  onNavigate,
}: {
  id: string;
  detail: FormatTemplateDetail | null;
  loading: boolean;
  onNavigate?: (id: string) => void;
}) {
  const navigable = Boolean(onNavigate);
  const handleNavigate = onNavigate ? () => onNavigate(id) : undefined;
  const handleKey = onNavigate
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onNavigate(id);
        }
      }
    : undefined;

  if (loading) {
    return (
      <div
        className={clsx(
          'flex items-center gap-2 p-3 rounded-xl border border-indigo-100 bg-indigo-50/40',
          navigable && 'cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/70 transition',
        )}
        onClick={handleNavigate}
        onKeyDown={handleKey}
        role={navigable ? 'button' : undefined}
        tabIndex={navigable ? 0 : undefined}
      >
        <Loader2 size={11} className="animate-spin text-indigo-400 shrink-0" />
        <span className="font-mono text-[10px] text-indigo-400 flex-1 min-w-0 truncate">{id}</span>
        {navigable && <ArrowUpRight size={11} className="text-indigo-400 shrink-0" />}
      </div>
    );
  }

  if (!detail) {
    return (
      <div
        className={clsx(
          'flex items-center gap-2 p-3 rounded-xl border border-indigo-100 bg-indigo-50/40',
          navigable && 'cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/70 transition',
        )}
        onClick={handleNavigate}
        onKeyDown={handleKey}
        role={navigable ? 'button' : undefined}
        tabIndex={navigable ? 0 : undefined}
      >
        <Layers size={11} className="text-indigo-400 shrink-0" />
        <span className="font-mono text-[10px] text-indigo-500 flex-1 min-w-0 truncate">{id}</span>
        {navigable && <ArrowUpRight size={11} className="text-indigo-400 shrink-0" />}
      </div>
    );
  }

  const tierColors: Record<string, string> = {
    premium: 'bg-amber-50 text-amber-700 border-amber-200',
    reference: 'bg-sky-50 text-sky-700 border-sky-200',
    standard: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  const tierClass = tierColors[detail.quality_tier] ?? tierColors.standard;

  return (
    <div
      className={clsx(
        'rounded-xl border border-indigo-100 bg-indigo-50/30 overflow-hidden',
        navigable && 'cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/60 transition',
      )}
      onClick={handleNavigate}
      onKeyDown={handleKey}
      role={navigable ? 'button' : undefined}
      tabIndex={navigable ? 0 : undefined}
    >
      <div className="flex items-start gap-3 p-3">
        {/* Exemplar thumbnail */}
        <div className="w-12 h-12 shrink-0 rounded-lg overflow-hidden bg-indigo-100 border border-indigo-200">
          {detail.hero_ad_image_url ? (
            <img
              src={detail.hero_ad_image_url}
              alt={detail.template_name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Layers size={14} className="text-indigo-300" />
            </div>
          )}
        </div>

        {/* Name + description */}
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-indigo-900 leading-snug truncate">
            {detail.template_name || id}
          </p>
          {detail.description && (
            <p className="text-[10px] text-slate-500 mt-0.5 leading-relaxed line-clamp-2">
              {detail.description}
            </p>
          )}
        </div>
        {navigable && (
          <ArrowUpRight size={12} className="text-indigo-400 shrink-0 mt-0.5" />
        )}
      </div>

      {/* Attribute chips row */}
      <div className="px-3 pb-3 flex flex-wrap gap-1.5">
        {detail.quality_tier && (
          <span className={clsx('text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded border', tierClass)}>
            {detail.quality_tier}
          </span>
        )}
        {detail.funnel_stage && (
          <span className="text-[9px] font-medium px-1.5 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-200 uppercase">
            {detail.funnel_stage.replace(/_/g, ' ')}
          </span>
        )}
        {detail.category_scope && (
          <span className="text-[9px] font-medium px-1.5 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200">
            {detail.category_scope.replace(/_/g, ' ')}
          </span>
        )}
        {detail.zone_count > 0 && (
          <span className="text-[9px] font-medium px-1.5 py-0.5 rounded border bg-teal-50 text-teal-700 border-teal-200">
            {detail.zone_count} zone{detail.zone_count !== 1 ? 's' : ''}
          </span>
        )}
        {detail.recommended_for_channels.slice(0, 3).map((ch) => (
          <span key={ch} className="text-[9px] font-medium px-1.5 py-0.5 rounded border bg-slate-50 text-slate-500 border-slate-200">
            {ch}
          </span>
        ))}
      </div>

      {/* Zone names */}
      {detail.zone_names.length > 0 && (
        <div className="px-3 pb-3 -mt-1">
          <p className="text-[9px] font-bold uppercase text-slate-400 mb-1">Zones</p>
          <p className="text-[10px] text-slate-500">{detail.zone_names.join(' · ')}</p>
        </div>
      )}

      {/* Template ID in muted mono */}
      <div className="px-3 pb-2.5">
        <p className="font-mono text-[9px] text-slate-300">{id}</p>
      </div>
    </div>
  );
}

// ─── MetaRow ────────────────────────────────────────────────────────────────

function MetaRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-[10px] font-bold uppercase text-slate-400 w-24 shrink-0 pt-0.5">
        {label}
      </span>
      <span
        className={clsx(
          'text-[11px] leading-snug text-slate-700 break-all',
          mono && 'font-mono text-[10px]',
        )}
      >
        {value}
      </span>
    </div>
  );
}
